import { ImageStyle, Platform, PlatformOSType, StyleSheet, TextStyle, ViewStyle } from 'react-native';

interface WebSpecificStyles {
  cursor?: string;
  outlineColor?: string;
  outlineOffset?: number | string;
  outlineStyle?: string;
  outlineWidth?: number | string;
}

type TSpecifyType = 'view' | 'text' | 'image' | undefined;
// Input stays permissive: when no type is specified, accept any style prop
// (View/Text/Image) so callers can pass e.g. fontSize without specifying 'text'.
type TStyleInput<ST extends TSpecifyType> = (ST extends 'view' ? ViewStyle : ST extends 'text' ? TextStyle : ST extends 'image' ? ImageStyle : ViewStyle | TextStyle | ImageStyle) &
  WebSpecificStyles;
// Return defaults to ViewStyle when unspecified so the result flows into
// style/containerStyle props. NON-distributive ([ST] extends [..]) on purpose:
// when _specifyType is omitted, ST infers as the whole 'view'|'text'|'image'|undefined
// union and a distributive conditional would return ViewStyle | TextStyle | ImageStyle,
// which (under React 19 / RN 0.79) no longer assigns to StyleProp<ViewStyle>.
// Text/image callers that need those props pass the explicit _specifyType.
type TStyleReturn<ST extends TSpecifyType> = ([ST] extends ['text'] ? TextStyle : [ST] extends ['image'] ? ImageStyle : ViewStyle) & WebSpecificStyles;

/**
 * This function is similar to Platform.select with some extra stuff and added support for Typescript validation for styles.
 * @param specifics array of styles that are specific to a platform. Accepted platforms are all the PlatformOSType values and 'mobile'.
 * @param _specifyType Optional type if you need to specify the type of style you are passing in and want to get the correct type back.
 * Helpful for when you want to specify specific style you want returned like TextStyle, ViewStyle and so on.
 * @returns Styles object
 */
const mbPlatformStyle = <ST extends TSpecifyType = undefined>(specifics: { [platform in PlatformOSType | 'mobile']?: TStyleInput<ST> }, _specifyType?: ST): TStyleReturn<ST> => {
  if (specifics.mobile) {
    if (Platform.OS === 'android' || Platform.OS === 'ios') {
      return StyleSheet.flatten([specifics.mobile, specifics[Platform.OS]]) as TStyleReturn<ST>;
    }
  }

  return (specifics[Platform.OS] ?? {}) as TStyleReturn<ST>;
};

export { mbPlatformStyle };
